"use client";

/**
 * The SVG refraction filter - leonardo.ai's optic, verbatim.
 *
 * Read off their live site (GlossySurface, 2026-08-28): an `feImage`
 * displacement map drawn to the element's measured box, three per-channel
 * `feDisplacementMap`s at scales -60/-48/-36 screened back together, and the
 * whole thing used as `backdrop-filter: url(#id) blur(...)` - refraction
 * FIRST, frost after. The Liquid Glass header pill mounts exactly this and
 * puts the url() in front of its own frost (`liquid/index.tsx`), gated on
 * `supportsLiquidGlass()` and on having measured the pill. The CRM call window
 * mounts it too.
 *
 * ⚠ BROWSER SPLIT (both states measured, do not "simplify" either away):
 *   - Current STABLE Chrome rasterises the feImage map and refracts -
 *     leonardo.ai visibly bends and splits colour there.
 *   - Chromium 148 (the embedded Browser pane) never rasterises `feImage` -
 *     not in backdrop-filter, not in filter, not via href/xlink/data/blob. An
 *     empty map is not "no displacement": every channel reads 0 and the pane
 *     shifts by a constant `-scale / 2`, which the frost hides. The FROST
 *     ITSELF SURVIVES there, url() and all (A/B stripe probe, 2026-08-28), so
 *     the worst case is a plain frosted pane - never a hole, and never a
 *     painted fake spectrum standing in for the real thing.
 *   - Safari/Firefox draw nothing for url() in a backdrop-filter; gate on
 *     `supportsLiquidGlass()`.
 *
 * Two leonardo quirks are kept ON PURPOSE (their production look has them):
 * the map paints its vertical ramp into BLUE while the filter reads
 * `yChannelSelector="G"`, so the vertical bend never engages, and the neutral
 * core sits at 0.75 alpha, letting the horizontal ramp bleed through the
 * middle. The CRM call window (`CallGlass.tsx`) also still mounts this filter.
 */

import { useMemo } from "react";

/** Chromium detection: a backdrop-filter referencing SVG only ever rendered
 *  there. (Safari/Firefox parse the syntax but draw nothing, so this is a UA
 *  check rather than `CSS.supports`.) False during SSR - the effect hydrates in
 *  on the client. */
export function supportsLiquidGlass(): boolean {
  if (typeof window === "undefined" || typeof navigator === "undefined") return false;
  if (!window.CSS?.supports?.("backdrop-filter", "blur(1px)") && !window.CSS?.supports?.("-webkit-backdrop-filter", "blur(1px)")) return false;
  const ua = navigator.userAgent;
  const safari = /safari/i.test(ua) && !/chrome|chromium|crios|edg/i.test(ua);
  const firefox = /firefox|fxios/i.test(ua);
  return !safari && !firefox;
}

/** The displacement map, as a data URI: the R channel is the x offset, G/B the
 *  y offset. The edges "bend"; the middle is a blurred neutral grey (50% = no
 *  displacement). */
export function displacementMapUri(w: number, h: number, radius: number): string {
  const edge = 2;
  const svg =
    `<svg viewBox="0 0 ${w} ${h}" xmlns="http://www.w3.org/2000/svg">` +
    `<defs>` +
    `<linearGradient id="r" x1="100%" y1="0%" x2="0%" y2="0%"><stop offset="0%" stop-color="#0000"/><stop offset="100%" stop-color="red"/></linearGradient>` +
    `<linearGradient id="b" x1="0%" y1="0%" x2="0%" y2="100%"><stop offset="0%" stop-color="#0000"/><stop offset="12%" stop-color="#0000"/><stop offset="88%" stop-color="blue"/><stop offset="100%" stop-color="blue"/></linearGradient>` +
    `</defs>` +
    `<rect width="${w}" height="${h}" fill="black"/>` +
    `<rect width="${w}" height="${h}" rx="${radius}" fill="url(#r)"/>` +
    `<rect width="${w}" height="${h}" rx="${radius}" fill="url(#b)" style="mix-blend-mode:difference"/>` +
    `<rect x="${edge}" y="${edge}" width="${Math.max(0, w - edge * 2)}" height="${Math.max(0, h - edge * 2)}" rx="${radius}" fill="hsl(0 0% 50% / 0.75)" style="filter:blur(6px)"/>` +
    `</svg>`;
  return `data:image/svg+xml,${encodeURIComponent(svg)}`;
}

export interface LiquidGlassFilterProps {
  /** The filter's own id, for `backdrop-filter: url(#id)`. Must be unique. */
  id: string;
  /** The pane's size in px - the map is drawn to it. */
  width: number;
  height: number;
  /** Corner radius (px) - MUST match the pane's own `border-radius`. */
  radius: number;
  /** Refraction strength (the R channel's displacement scale; G/B follow). */
  strength?: number;
}

/**
 * The refraction filter on its own, with no surface attached.
 *
 * Renders nothing visible: a zero-sized `<svg>` holding a `<defs>`. Whoever
 * mounts it is responsible for putting `url(#id)` in a `backdrop-filter`.
 */
export function LiquidGlassFilter({ id, width, height, radius, strength = 60 }: LiquidGlassFilterProps) {
  const mapUri = useMemo(() => displacementMapUri(width, height, radius), [width, height, radius]);
  return (
    <svg aria-hidden style={{ position: "absolute", width: 0, height: 0 }} xmlns="http://www.w3.org/2000/svg">
      <defs>
        <filter id={id} colorInterpolationFilters="sRGB" x="-15%" y="-15%" width="130%" height="130%">
          <feImage x="0" y="0" width="100%" height="100%" preserveAspectRatio="none" result="map" href={mapUri} />
          <feDisplacementMap in="SourceGraphic" in2="map" result="dr" scale={-strength} xChannelSelector="R" yChannelSelector="G" />
          <feColorMatrix in="dr" type="matrix" values="1 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 1 0" result="red" />
          <feDisplacementMap in="SourceGraphic" in2="map" result="dg" scale={-Math.round(strength * 0.8)} xChannelSelector="R" yChannelSelector="G" />
          <feColorMatrix in="dg" type="matrix" values="0 0 0 0 0 0 1 0 0 0 0 0 0 0 0 0 0 0 1 0" result="green" />
          <feDisplacementMap in="SourceGraphic" in2="map" result="db" scale={-Math.round(strength * 0.6)} xChannelSelector="R" yChannelSelector="G" />
          <feColorMatrix in="db" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 1 0 0 0 0 0 1 0" result="blue" />
          <feBlend in="red" in2="green" mode="screen" result="rg" />
          <feBlend in="rg" in2="blue" mode="screen" result="out" />
          <feGaussianBlur in="out" stdDeviation="1.05" />
        </filter>
      </defs>
    </svg>
  );
}
